// FILE: apps/admin/src/components/OperatorAlertsMenu.tsx
// VERSION: 1.0.0
// START_MODULE_CONTRACT
//   PURPOSE: Topbar operator alerts bell menu with unread count badge, quick acknowledge, and runbook links.
//   SCOPE: OperatorAlertsMenu component polling /admin/alerts.
//   DEPENDS: ../api.js
//   LINKS: M-ADMIN
//   ROLE: RUNTIME
//   MAP_MODE: EXPORTS
// END_MODULE_CONTRACT
//
// START_MODULE_MAP
//   OperatorAlertsMenu - Topbar notification bell dropdown for operator alerts
// END_MODULE_MAP

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { api, dt } from '../api.js';

interface AlertItem {
  id: string;
  kind: string;
  severity: 'CRITICAL' | 'HIGH' | 'WARN' | 'INFO';
  title: string;
  message: string;
  deepLinkPath: string | null;
  acknowledgedAt: string | null;
  createdAt: string;
}

interface AlertsListResponse {
  items: AlertItem[];
  unreadCount: number;
}

// START_CONTRACT: OperatorAlertsMenu
//   PURPOSE: Render topbar operator alerts bell with unread count badge and quick-acknowledge popover.
//   INPUTS: none
//   OUTPUTS: React element
//   SIDE_EFFECTS: Polls /admin/alerts and posts acknowledgements
//   LINKS: M-ADMIN
// END_CONTRACT: OperatorAlertsMenu
export function OperatorAlertsMenu() {
  // START_BLOCK_OPERATOR_ALERTS_MENU
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const qc = useQueryClient();
  const navigate = useNavigate();

  useEffect(() => {
    const onPointerDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      window.removeEventListener('keydown', onKeyDown);
    };
  }, []);

  const { data } = useQuery({
    queryKey: ['admin-alerts-bell'],
    queryFn: () => api.get<AlertsListResponse>('/admin/alerts'),
    refetchInterval: 20_000,
  });

  const ackAll = useMutation({
    mutationFn: () => api.post('/admin/alerts/ack-all'),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin-alerts-bell'] });
      void qc.invalidateQueries({ queryKey: ['admin-alerts'] });
    },
  });

  const unread = data?.unreadCount ?? 0;
  const recent = (data?.items ?? []).slice(0, 6);

  return (
    <div ref={rootRef} style={{ position: 'relative' }}>
      <button
        type="button"
        className="icon-btn"
        title="Уведомления и алерты"
        aria-label="Уведомления и алерты"
        onClick={() => setOpen((v) => !v)}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>
        {unread > 0 && <span className="alert-badge">{unread}</span>}
      </button>

      {open && (
        <div
          className="card alerts-popover"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="row" style={{ justifyContent: 'space-between', marginBottom: 10 }}>
            <b>Оперативные уведомления ({unread})</b>
            <div className="row" style={{ gap: 6 }}>
              {unread > 0 && (
                <button
                  className="btn ghost sm"
                  disabled={ackAll.isPending}
                  onClick={() => ackAll.mutate()}
                >
                  Прочитать все
                </button>
              )}
              <button
                className="btn ghost sm"
                onClick={() => {
                  setOpen(false);
                  navigate('/alerts');
                }}
              >
                Все (25 Runbook) →
              </button>
            </div>
          </div>

          {!recent.length ? (
            <div className="muted" style={{ padding: '14px 4px', textAlign: 'center' }}>
              Новых системных алертов нет
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {recent.map((a) => (
                <div
                  key={a.id}
                  className="alert-popover-item"
                  style={{ opacity: a.acknowledgedAt ? 0.65 : 1 }}
                  onClick={() => {
                    setOpen(false);
                    navigate(a.deepLinkPath ?? '/alerts');
                  }}
                >
                  <div className="row" style={{ justifyContent: 'space-between' }}>
                    <span className={`chip severity-${a.severity}`}>{a.severity}</span>
                    <span className="muted" style={{ fontSize: 11 }}>{dt(a.createdAt)}</span>
                  </div>
                  <div style={{ fontWeight: 700, marginTop: 4 }}>{a.title}</div>
                  <div className="muted" style={{ fontSize: 12.5 }}>{a.message}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
  // END_BLOCK_OPERATOR_ALERTS_MENU
}
